import { useEffect, useState, useMemo } from "react";
import { Search, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { getEmployeesWithResourceShareStatus } from "@/api/getEmployeesWithResourceShareStatus/getEmployeesWithResourceShareStatusApi";
import { shareResourceToEmployees } from "@/api/shareResourceToEmployees/shareResourceToEmployeesApi";
import { unshareResourceFromEmployees } from "@/api/unshareResourceFromEmployees/unshareResourceFromEmployeesApi";
import { EmployeeWithShareStatus } from "@/api/getEmployeesWithResourceShareStatus/types";

interface ShareToEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceUuid: string;
  resourceTitle: string;
}

export default function ShareToEmployeeModal({
  isOpen,
  onClose,
  resourceUuid,
  resourceTitle,
}: ShareToEmployeeModalProps) {
  const [employees, setEmployees] = useState<EmployeeWithShareStatus[]>([]);
  const [initialSharedUuids, setInitialSharedUuids] = useState<Set<string>>(
    new Set(),
  );
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch employees when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen, resourceUuid]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response =
        await getEmployeesWithResourceShareStatus(resourceUuid);
      setEmployees(response.data.employees);

      // Set initial shared employees
      const sharedUuids = new Set(
        response.data.employees
          .filter((emp) => emp.isShared)
          .map((emp) => emp.uuid),
      );
      setInitialSharedUuids(sharedUuids);
      setSelectedUuids(new Set(sharedUuids));
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;

    const search = searchTerm.toLowerCase();
    return employees.filter(
      (emp) =>
        emp.firstName.toLowerCase().includes(search) ||
        emp.lastName.toLowerCase().includes(search) ||
        emp.email.toLowerCase().includes(search),
    );
  }, [employees, searchTerm]);

  const handleSelectAll = () => {
    setSelectedUuids(new Set(filteredEmployees.map((emp) => emp.uuid)));
  };

  const handleDeselectAll = () => {
    setSelectedUuids(new Set());
  };

  const handleToggleEmployee = (uuid: string) => {
    const newSelected = new Set(selectedUuids);
    if (newSelected.has(uuid)) {
      newSelected.delete(uuid);
    } else {
      newSelected.add(uuid);
    }
    setSelectedUuids(newSelected);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Calculate what changed
      const toShare = Array.from(selectedUuids).filter(
        (uuid) => !initialSharedUuids.has(uuid),
      );
      const toUnshare = Array.from(initialSharedUuids).filter(
        (uuid) => !selectedUuids.has(uuid),
      );

      const promises = [];

      if (toShare.length > 0) {
        promises.push(
          shareResourceToEmployees({
            resourceUuid,
            employeeUuids: toShare,
          }),
        );
      }

      if (toUnshare.length > 0) {
        promises.push(
          unshareResourceFromEmployees({
            resourceUuid,
            employeeUuids: toUnshare,
          }),
        );
      }

      if (promises.length === 0) {
        toast.info("No changes to save");
        onClose();
        return;
      }

      const results = await Promise.all(promises);

      // Build success message
      const messages = [];
      if (toShare.length > 0 && results[0]) {
        messages.push(results[0].data.message);
      }
      if (toUnshare.length > 0) {
        const unshareResult = toShare.length > 0 ? results[1] : results[0];
        if (unshareResult) {
          messages.push(unshareResult.data.message);
        }
      }

      toast.success(messages.join(". "));
      onClose();
    } catch (error) {
      console.error("Error saving share status:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog onOpenChange={handleClose} open={isOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Share &quot;{resourceTitle}&quot; with Employees</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Search and Select All/Deselect All */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-9"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search employees..."
                value={searchTerm}
              />
            </div>
            <Button
              onClick={handleSelectAll}
              size="sm"
              type="button"
              variant="outline"
            >
              Select All
            </Button>
            <Button
              onClick={handleDeselectAll}
              size="sm"
              type="button"
              variant="outline"
            >
              Deselect All
            </Button>
          </div>

          {/* Employee List */}
          <div className="flex-1 overflow-y-auto border rounded-md">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                {searchTerm ? "No employees found" : "No employees available"}
              </div>
            ) : (
              <div className="divide-y">
                {filteredEmployees.map((employee) => (
                  <div
                    className="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    key={employee.uuid}
                    onClick={() => handleToggleEmployee(employee.uuid)}
                  >
                    <Checkbox
                      checked={selectedUuids.has(employee.uuid)}
                      onCheckedChange={() =>
                        handleToggleEmployee(employee.uuid)
                      }
                    />
                    <div className="flex-1">
                      <div className="font-medium">
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {employee.email}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button disabled={saving} onClick={handleClose} variant="outline">
            Cancel
          </Button>
          <Button disabled={saving || loading} onClick={handleSave}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
